import React, { useState, useEffect } from 'react';
import ComboBox from '../components/ComboBox';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import JobSearchSelect from './JobSearchSelect';
import { jobLabel } from '../utils/ui';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}


export default function CreatePO() {
  const navigate = useNavigate();
  const { worker } = useAuth();
  // Writing a PO and putting something on the card are the same errand — you are
  // standing in a supply house buying something for a job — so they share a screen.
  // ?mode=card opens straight on Credit Card (a Company Answer's "Take me there", Oct 6 2026).
  const [mode, setMode] = useState(() => { try { return new URLSearchParams(window.location.search).get('mode') === 'card' ? 'card' : 'po'; } catch { return 'po'; } });   // 'po' | 'card'
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [isRental, setIsRental] = useState(false);
  const [isMajor, setIsMajor] = useState(false);
  const [rentalStart, setRentalStart] = useState('');
  const [notifyShop, setNotifyShop] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);   // the ⧉ on the PO Complete screen just fired
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      let list = [];
      try { const data = await apiFetch('/projects?scope=current'); list = Array.isArray(data) ? data : []; } catch {}
      if (!mounted) return;
      setProjects(list);
      let defaultId = list.length === 1 ? list[0].id : '';
      // Default to the job you're clocked into — but only if it's actually in the
      // pickable list, otherwise the Job field would render blank.
      try { const s = await apiFetch('/time/status'); if (s && s.projectId && list.some(p => p.id === s.projectId)) defaultId = s.projectId; } catch {}
      if (mounted) setProjectId(prev => prev || defaultId);
    })();
    apiFetch('/vendors/active').then(data => { if (mounted) setVendors(data || []); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!projectId) { setError('Please select a project.'); return; }
    if (!description.trim()) { setError('Please enter a description.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const result = await apiFetch('/purchase-orders', {
        method: 'POST',
        body: JSON.stringify({ status: 'Received', projectId, description: description.trim(), vendor: vendor.trim() || null, cost: cost !== '' ? cost : null, notes: notes.trim() || null, is_rental: isRental ? 1 : 0, rental_start: isRental ? (rentalStart || null) : null, notify_shop: notifyShop ? 1 : 0, is_major: isMajor ? 1 : 0 }),
      });
      setCreated(result);
    } catch (err) {
      setError(err?.error || 'Failed to create PO. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (created) {
    return (
      <div className="page" style={{ background: '#fff' }}>
        <div className="top-bar">
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#111' }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M14 4l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="top-bar-wordmark" style={{ flex: 1, textAlign: 'center' }}>TITAN <span>ELECTRIC</span></div>
          <div style={{ width: 22 }} />
        </div>
        <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none"><path d="M8 18l8 8 12-14" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          {(() => {
            // What gets read out to the vendor, and what Copy puts on the clipboard:
            // "PO #20720 · WO26132 — Taylor House".
            const jobLine = jobLabel({ project_number: created.project_number, name: created.project_name });
            const copyText = `PO #${created.po_number}${jobLine ? ' · ' + jobLine : ''}`;
            const copy = async () => {
              try { await navigator.clipboard.writeText(copyText); }
              catch {
                const ta = document.createElement('textarea'); ta.value = copyText; ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch {} document.body.removeChild(ta);
              }
              setCopied(true); setTimeout(() => setCopied(false), 1800);
            };
            return (
              <div style={{ width: '100%', maxWidth: 420 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a', marginBottom: 4 }}>PO Complete</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, margin: '8px 0 2px' }}>
                  <div style={{ fontSize: 36, fontWeight: 900, color: '#e87722', letterSpacing: 1 }}>PO #{created.po_number}</div>
                  <button onClick={copy} title="Copy the PO number and job to the clipboard" aria-label="Copy"
                    style={{ width: 44, height: 44, borderRadius: 12, border: '1.5px solid ' + (copied ? '#16a34a' : '#e5e7eb'), background: copied ? '#f0fdf4' : '#fff', color: copied ? '#16a34a' : '#374151', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {copied ? '✓' : '⧉'}
                  </button>
                </div>
                {jobLine && <div style={{ fontSize: 17, fontWeight: 800, color: '#111', marginBottom: 4 }}>{jobLine}</div>}
                <div style={{ fontSize: 14, color: '#666', lineHeight: 1.5 }}>{created.description}</div>
                <div style={{ fontSize: 12, color: copied ? '#16a34a' : '#9ca3af', marginTop: 8, fontWeight: copied ? 700 : 500 }}>{copied ? 'Copied — paste it into your text or email.' : 'Tap ⧉ to copy "' + copyText + '".'}</div>
              </div>
            );
          })()}
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={() => { setCreated(null); setDescription(''); setVendor(''); setCost(''); setNotes(''); setIsRental(false); setRentalStart(''); setNotifyShop(false); setProjectId(projects.length === 1 ? projects[0].id : ''); }}
              style={{ padding: '12px 24px', borderRadius: 10, border: '2px solid #e87722', background: '#fff', color: '#e87722', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >Create Another</button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: '#e87722', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >Back to Jobs</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ background: '#fff' }}>
      <div className="top-bar">
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#111' }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M14 4l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="top-bar-wordmark" style={{ flex: 1, textAlign: 'center' }}>TITAN <span>ELECTRIC</span></div>
        <div style={{ width: 22 }} />
      </div>

      <div style={{ padding: '24px 20px calc(110px + env(safe-area-inset-bottom, 0px))' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111' }}>
            {mode === 'card' ? 'Card or Out of Pocket' : 'Create Purchase Order'}
          </div>
          <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
            {mode === 'card'
              ? 'Something bought on the spot. It lands on the job with the POs.'
              : 'PO numbers are assigned automatically by the system.'}
          </div>
        </div>

        {/* Two errands, one screen. A card charge is not a PO — no vendor, no number,
            no approval — so it gets its own short form rather than more fields on this one. */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
          {[['po', '🧾 Purchase Order'], ['card', '💳 Credit Card']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              style={{ flex: 1, padding: '13px 10px', borderRadius: 12, cursor: 'pointer',
                border: mode === k ? '2px solid #0e7490' : '1.5px solid #e5e7eb',
                background: mode === k ? '#ecfeff' : '#fff',
                color: mode === k ? '#0e7490' : '#6b7280', fontSize: 15, fontWeight: 800 }}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'card' && (
          <CardCharge projects={projects} projectId={projectId} setProjectId={setProjectId}
            onDone={() => navigate('/dashboard')} />
        )}

        {mode === 'po' && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Job *</label>
            <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this PO for?"
              rows={4}
              style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Vendor</label>
              <ComboBox
                value={vendor}
                onChange={(val, label) => setVendor(val || label)}
                options={vendors.map(v => ({ value: v.name, label: v.name }))}
                placeholder="Search vendors…"
                size="mobile"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Cost ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={cost}
                onChange={e => setCost(e.target.value)}
                placeholder="0.00 (optional)"
                style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Additional Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details… (optional)"
              rows={3}
              style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
            />
          </div>

          <div style={{ border: '1.5px solid #ede9fe', background: '#faf5ff', borderRadius: 12, padding: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700, color: '#6d28d9' }}>
              <input type="checkbox" checked={isRental} onChange={e => setIsRental(e.target.checked)} style={{ width: 20, height: 20 }} />
              This PO is for rented equipment
            </label>
            {isRental && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rental start date</label>
                <input type="date" value={rentalStart} onChange={e => setRentalStart(e.target.value)}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 6 }}>A card will be added to Shop → Equipment → Rented Equipment.</div>
              </div>
            )}
          </div>

          <div style={{ border: '1.5px solid #fee2e2', background: '#fef2f2', borderRadius: 12, padding: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700, color: '#b91c1c' }}>
              <input type="checkbox" checked={isMajor} onChange={e => setIsMajor(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1 }} />
              <span>This is a Major PO<br /><span style={{ fontSize: 12, fontWeight: 500, color: '#ef4444' }}>Big, higher-dollar orders with multiple ship dates — tracked & filtered separately.</span></span>
            </label>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 14 }}>
            <input type="checkbox" checked={notifyShop} onChange={e => setNotifyShop(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1 }} />
            <span><span style={{ fontWeight: 700 }}>Notify the shop when a ship date is set</span><br /><span style={{ fontSize: 12, color: '#9ca3af' }}>For big orders that need coordination.</span></span>
          </label>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{ marginTop: 8, padding: '15px', borderRadius: 12, border: 'none', background: submitting ? '#ccc' : '#e87722', color: '#fff', fontSize: 17, fontWeight: 800, cursor: submitting ? 'not-allowed' : 'pointer', letterSpacing: 0.3 }}
          >
            {submitting ? 'Submitting…' : 'Submit PO'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

// Bought on the spot: the job, what it was, what it cost, and a picture of the receipt.
// Deliberately four things. This gets filled in standing at a counter with one hand,
// so anything that isn't needed to account for the money later is left off.
function CardCharge({ projects, projectId, setProjectId, onDone }) {
  const [paidWith, setPaidWith] = useState('company_card');
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  // The receipt is read the moment it is picked (Pat, Sep 27 2026): what it was, the
  // amount and where from get filled in for the person to check. Only empty boxes are
  // filled — anything already typed stays.
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState('');
  const [filled, setFilled] = useState({});     // which boxes the reader filled
  async function pickReceipt(file) {
    setReceipt(file || null); setReadNote(''); setFilled({});
    if (!file) return;
    setReading(true);
    try {
      const fd = new FormData(); fd.append('photo', file, file.name || 'receipt.jpg');
      const r = await fetch(`${API_BASE}/receipts/read`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setReadNote(j.error || 'Could not read the receipt — type it in.'); return; }
      if (j.not_receipt) { setReadNote('That does not look like a receipt — type it in.'); return; }
      const got = {};
      if (j.total != null && !cost) { setCost(String(j.total.toFixed(2))); got.cost = true; }
      if (j.vendor && !vendor) { setVendor(j.vendor); got.vendor = true; }
      if (j.items && j.items.length && !description) { setDescription(j.items.join(', ')); got.description = true; }
      setFilled(got);
      const n = Object.keys(got).length;
      setReadNote(n ? `Read from the receipt${j.confidence === 'low' ? ' (hard to read — check it)' : ''}: ${[got.description && 'what it was', got.cost && 'the amount', got.vendor && 'where from'].filter(Boolean).join(', ')}. Check and fix anything wrong.` : 'Read the receipt, but the boxes were already filled in.');
    } catch { setReadNote('Could not read the receipt — type it in.'); }
    finally { setReading(false); }
  }
  const readMark = key => (filled[key] ? { borderColor: '#16a34a', background: '#f0fdf4' } : {});

  const lbl = { display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
  const inp = { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '13px 12px', fontSize: 16, fontFamily: 'inherit' };

  async function save(e) {
    e.preventDefault();
    if (!projectId) { setErr('Which job is this for?'); return; }
    if (!cost || Number(cost) <= 0) { setErr('How much was it?'); return; }
    setErr(''); setSaving(true);
    try {
      // Multipart, because of the receipt — so this one can't go through apiFetch.
      const fd = new FormData();
      fd.append('project_id', projectId);
      fd.append('paid_with', paidWith);
      fd.append('description', description.trim());
      fd.append('cost', String(Number(cost)));
      if (vendor.trim()) fd.append('vendor', vendor.trim());
      if (receipt) fd.append('receipt', receipt);
      const r = await fetch(`${API_BASE}/card-charges`, {
        method: 'POST', headers: { 'x-worker-token': getToken() || '' }, body: fd,
      });
      if (!r.ok) throw await r.json().catch(() => ({ error: 'Could not save that.' }));
      setDone(true);
    } catch (e2) { setErr(e2?.error || 'Could not save that.'); }
    setSaving(false);
  }

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <div style={{ fontSize: 46, marginBottom: 10 }}>✅</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>Logged</div>
        <div style={{ fontSize: 14, color: '#666', margin: '6px 0 22px' }}>
          The office has it. It counts against the job, but it is not on the PO list
          for everyone to read.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { setDone(false); setDescription(''); setCost(''); setVendor(''); setReceipt(null); }}
            style={{ flex: 1, padding: 15, borderRadius: 12, border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
            Add another
          </button>
          <button onClick={onDone}
            style={{ flex: 1, padding: 15, borderRadius: 12, border: 'none', background: '#0e7490', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <label style={lbl}>Receipt first</label>
        {/* Photo first (Pat, Sep 27 2026): the camera, or a receipt already in the phone's
            photos. Either way it is read straight away and the lines below fill in. */}
        {receipt ? (
          <label style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: '#0e7490', fontWeight: 700, borderStyle: 'dashed', textAlign: 'center' }}>
            🧾 {receipt.name} · change
            <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => pickReceipt(e.target.files?.[0] || null)} />
          </label>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ ...inp, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', color: '#0e7490', fontWeight: 800, borderStyle: 'dashed', borderColor: '#0e7490', background: '#ecfeff', textAlign: 'center' }}>
              📷 Take a photo
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => pickReceipt(e.target.files?.[0] || null)} />
            </label>
            <label style={{ ...inp, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', color: '#9ca3af', fontWeight: 700, borderStyle: 'dashed', textAlign: 'center' }}>
              🖼️ From my photos
              <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => pickReceipt(e.target.files?.[0] || null)} />
            </label>
          </div>
        )}
        {reading && <div style={{ fontSize: 13, color: '#0e7490', fontWeight: 700, marginTop: 8 }}>⏳ Reading the receipt…</div>}
        {!reading && readNote && <div style={{ fontSize: 13, color: Object.keys(filled).length ? '#15803d' : '#b45309', fontWeight: 600, marginTop: 8 }}>{Object.keys(filled).length ? '✓ ' : ''}{readNote}</div>}
      </div>

      <div>
        <label style={lbl}>Job *</label>
        <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
      </div>

      <div>
        <label style={lbl}>Paid with</label>
        {/* Out of pocket is the one that has to be paid back, so it is asked here rather
            than worked out later from who wrote it. */}
        <div style={{ display: 'flex', gap: 8 }}>
          {[['company_card', 'Company card'], ['own_money', 'My own money']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPaidWith(k)}
              style={{ flex: 1, padding: '13px 10px', borderRadius: 10, cursor: 'pointer',
                border: paidWith === k ? '2px solid #0e7490' : '1.5px solid #e5e7eb',
                background: paidWith === k ? '#ecfeff' : '#fff',
                color: paidWith === k ? '#0e7490' : '#6b7280', fontSize: 15, fontWeight: 700 }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={lbl}>What was it? *</label>
        <input value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Fittings, fuel, lunch for the crew…" style={{ ...inp, ...readMark('description') }} />
      </div>

      <div>
        <label style={lbl}>Amount *</label>
        <input type="number" inputMode="decimal" step="0.01" value={cost}
          onChange={e => setCost(e.target.value)} placeholder="0.00" style={{ ...inp, ...readMark('cost') }} />
      </div>

      <div>
        <label style={lbl}>Where from</label>
        <input value={vendor} onChange={e => setVendor(e.target.value)}
          placeholder="Optional — the shop or supplier" style={{ ...inp, ...readMark('vendor') }} />
      </div>

      {err && <div style={{ color: '#dc2626', fontSize: 14, fontWeight: 600 }}>{err}</div>}

      <button type="submit" disabled={saving}
        style={{ padding: 16, borderRadius: 12, border: 'none', background: saving ? '#8ab8c2' : '#0e7490',
          color: '#fff', fontSize: 17, fontWeight: 800, cursor: saving ? 'default' : 'pointer' }}>
        {saving ? 'Saving…' : 'Log it'}
      </button>
    </form>
  );
}
